import { InfoIcon } from "@chakra-ui/icons";
import {
  Box,
  Divider,
  Grid,
  FormControl,
  FormErrorMessage,
  FormLabel,
  GridItem,
  HStack,
  ListItem,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  SimpleGrid,
  Switch,
  Text,
  Tooltip,
  UnorderedList,
  chakra,
} from "@chakra-ui/react";
import { HasAccess } from "@intentsify/authorization/dist/react";
import {
  CampaignAdvancedSettings,
  CampaignDeliveryRecipientType,
  CompanyPixel,
  DSPProvider,
  Dynamic1Status,
  ProgrammaticCampaign,
} from "@intentsify/types";
import { isPopulatedArray } from "@intentsify/utils";
import { Badge, FilterAsyncTokenBased } from "components";
import { forwardRef, useImperativeHandle, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { defaultListPixelsParams } from "screens";
import { listPaginatedProgrammaticCampaigns } from "screens/CampaignPacing/screens/displayPerformance.requests";
import { MutateCampaignAdvancedSettingsParams } from "screens/Campaigns/screens/CampaignsWizard/CampaignsWizard.requests";
import { useComponentColors } from "theme";
import { FetchDataParamsWithToken, WithCampaignId } from "types";
import { NavigationBlockers } from "../../NavigationBlockers/NavigationBlockers";
import { SubmitTrigger } from "../../SettingsStep/SettingsForm";
import { listCompanyPixels } from "../ActivationSettingsStep.requests";
import { CampaignActivationSettingsInputs } from "../ActivationSettingsStep.types";
import { useWatchActivationSettings } from "../ActivationSettingsStep.utils";
import { DeliveryRecipients } from "./DeliveryRecipients";
import { combineParamsAndFilters } from "utils";

type ActivationSettingsFormProps = {
  id: number;
  campaignAdvancedSettings: CampaignAdvancedSettings;
  onSubmit: (params: MutateCampaignAdvancedSettingsParams) => void;
  companyId: number;
  d1Status: Dynamic1Status;
};

const ActivationSettingsForm = forwardRef<
  SubmitTrigger | undefined,
  ActivationSettingsFormProps
>(({ id, campaignAdvancedSettings, onSubmit, companyId, d1Status }, ref) => {
  const componentColors = useComponentColors();

  const [intentDataDeliveryRecipients, setIntentDataDeliveryRecipients] =
    useState<string[]>(
      campaignAdvancedSettings.deliveryRecipients
        .filter((e) => e.type === CampaignDeliveryRecipientType.Intent)
        .map((e: { email: string }) => e.email)
    );

  const [hasAutomatedIntentDelivery, setHasAutomatedIntentDelivery] = useState(
    campaignAdvancedSettings.automatedIntentDeliveryEnabled
  );

  const [showBuyingGroupContactDetails, setShowBuyingGroupContactDetails] =
    useState(campaignAdvancedSettings.showBuyingGroupContactDetails);

  const [buyingGroupDeliveryRecipients, setBuyingGroupDeliveryRecipients] =
    useState<string[]>(
      campaignAdvancedSettings.deliveryRecipients
        .filter((e) => e.type === CampaignDeliveryRecipientType.BuyingGroup)
        .map((e: { email: string }) => e.email)
    );

  const [hasAutomatedBuyingGroupelivery, setHasAutomatedBuyingGroupelivery] =
    useState(campaignAdvancedSettings.automatedBuyingGroupDeliveryEnabled);

  const [
    leadActivationSummariesDeliveryRecipients,
    setLeadActivationSummariesDeliveryRecipients,
  ] = useState<string[]>(
    campaignAdvancedSettings.deliveryRecipients
      .filter(
        (e) => e.type === CampaignDeliveryRecipientType.LeadActivationSummaries
      )
      .map((e: { email: string }) => e.email)
  );

  const [
    hasLeadActivationSummariesDeliveryEnabled,
    setHasLeadActivationSummariesDeliveryEnabled,
  ] = useState(campaignAdvancedSettings.leadActivationSummariesDeliveryEnabled);

  const {
    watch,
    register,
    handleSubmit,
    formState: { errors },
    control,
    setValue,
  } = useForm<CampaignActivationSettingsInputs>({
    mode: "onBlur",
    defaultValues: {
      pixelIds: campaignAdvancedSettings.pixelIds,
      leadDomainCap: campaignAdvancedSettings.leadDomainCap,
      averageDealSize: campaignAdvancedSettings.averageDealSize,
      averageSalesCycleMonths: campaignAdvancedSettings.averageSalesCycleMonths,
      programmaticCampaigns: campaignAdvancedSettings.programmaticCampaigns.map(
        (programmaticCampaign) =>
          `${
            programmaticCampaign.programmaticCampaignId
          } (${programmaticCampaign.dspProvider.toUpperCase()})`
      ),
      innovidCampaigns: campaignAdvancedSettings.innovidCampaigns.map(
        (programmaticCampaign) =>
          `${
            programmaticCampaign.programmaticCampaignId
          } (${programmaticCampaign.dspProvider.toUpperCase()})`
      ),
      customAudienceEnabled: campaignAdvancedSettings.customAudienceEnabled,
      customAudiencePreviewEnabled:
        campaignAdvancedSettings.customAudiencePreviewEnabled,
      customInternationalAudienceEnabled:
        campaignAdvancedSettings.customInternationalAudienceEnabled,
    },
  });

  const leadDomainCap = watch("leadDomainCap");
  const selectedPixelIds = watch("pixelIds");
  const averageDealSize = watch("averageDealSize");
  const averageSalesCycleMonths = watch("averageSalesCycleMonths");
  const programmaticCampaigns = watch("programmaticCampaigns");
  const innovidCampaigns = watch("innovidCampaigns");
  const customAudienceEnabled = watch("customAudienceEnabled");
  const customAudiencePreviewEnabled = watch("customAudiencePreviewEnabled");
  const customInternationalAudienceEnabled = watch(
    "customInternationalAudienceEnabled"
  );

  const listPixels =
    (companyId: number) =>
    async (
      params: FetchDataParamsWithToken<keyof CompanyPixel> & {
        pageSize?: number;
      }
    ) => {
      const { companyPixels, nextPageToken } = await listCompanyPixels(
        companyId,
        params
      );

      return {
        nextPageToken,
        results: companyPixels.map((pixel) => ({
          value: pixel.pixelId,
          label: pixel.displayName,
        })),
      };
    };

  const listProgrammaticCampaigns =
    (dspProviders: DSPProvider[]) =>
    async (
      params: FetchDataParamsWithToken<keyof ProgrammaticCampaign> & {
        pageSize?: number;
      }
    ) => {
      const paramsAndFilters = combineParamsAndFilters({
        ...params,
        dspProvider: dspProviders,
      });

      const { data, nextPageToken } = await listPaginatedProgrammaticCampaigns(
        paramsAndFilters
      );
      return {
        nextPageToken,
        results: data.map((programmaticCampaign) => ({
          value: `${
            programmaticCampaign.id
          } (${programmaticCampaign.dspProvider.toUpperCase()})`,
          label:
            programmaticCampaign.displayName + ` (${programmaticCampaign.id})`,
          meta: {
            renderListItemAdditionalContent: () => (
              <HStack>
                <Badge height="23px" lineHeight="23px" textAlign="left">
                  {programmaticCampaign.dspProvider}
                </Badge>
              </HStack>
            ),
          },
        })),
      };
    };

  const newCampaignAdvancedSettings: CampaignAdvancedSettings = useMemo(() => {
    return {
      automatedIntentDeliveryEnabled: hasAutomatedIntentDelivery,
      automatedBuyingGroupDeliveryEnabled: hasAutomatedBuyingGroupelivery,
      leadActivationSummariesDeliveryEnabled:
        hasLeadActivationSummariesDeliveryEnabled,
      deliveryRecipients: [
        ...intentDataDeliveryRecipients.map((e) => ({
          email: e,
          type: CampaignDeliveryRecipientType.Intent,
        })),
        ...buyingGroupDeliveryRecipients.map((e) => ({
          email: e,
          type: CampaignDeliveryRecipientType.BuyingGroup,
        })),
        ...leadActivationSummariesDeliveryRecipients.map((e) => ({
          email: e,
          type: CampaignDeliveryRecipientType.LeadActivationSummaries,
        })),
      ],
      programmaticCampaigns: programmaticCampaigns.map(
        (programmaticCampaign) => {
          const [programmaticCampaignId, dspProvider] =
            programmaticCampaign.split(" (");
          return {
            programmaticCampaignId: programmaticCampaignId,
            dspProvider: dspProvider
              .replace(")", "")
              .toLowerCase() as DSPProvider,
          };
        }
      ),
      innovidCampaigns: innovidCampaigns.map((programmaticCampaign) => {
        const [programmaticCampaignId, dspProvider] =
          programmaticCampaign.split(" (");
        return {
          programmaticCampaignId: programmaticCampaignId,
          dspProvider: dspProvider
            .replace(")", "")
            .toLowerCase() as DSPProvider,
        };
      }),
      convertrCampaigns: [],
      pixelIds: selectedPixelIds,
      leadDomainCap,
      showBuyingGroupContactDetails,
      averageDealSize,
      averageSalesCycleMonths,
      customAudienceEnabled,
      customAudiencePreviewEnabled,
      customInternationalAudienceEnabled,
    };
  }, [
    hasAutomatedIntentDelivery,
    hasAutomatedBuyingGroupelivery,
    hasLeadActivationSummariesDeliveryEnabled,
    intentDataDeliveryRecipients,
    buyingGroupDeliveryRecipients,
    leadActivationSummariesDeliveryRecipients,
    programmaticCampaigns,
    innovidCampaigns,
    selectedPixelIds,
    leadDomainCap,
    showBuyingGroupContactDetails,
    averageDealSize,
    averageSalesCycleMonths,
    customAudienceEnabled,
    customAudiencePreviewEnabled,
    customInternationalAudienceEnabled,
  ]);

  useImperativeHandle(ref, () => ({
    submit({ shouldExitOnSuccess }) {
      handleSubmit((values) => {
        const newSettings: WithCampaignId<
          Omit<CampaignAdvancedSettings, "convertrCampaigns">
        > = { id: id, ...values, ...newCampaignAdvancedSettings };

        onSubmit({
          newSettings,
          shouldExitOnSuccess,
        });
      })();
    },
  }));

  const haveChanged = useWatchActivationSettings(
    campaignAdvancedSettings,
    newCampaignAdvancedSettings
  );

  return (
    <>
      <NavigationBlockers isUnsavedChangesBlockerEnabled={haveChanged} />

      <SimpleGrid>
        <GridItem colSpan={{ md: 2 }}>
          <chakra.form method="POST" borderTopRadius={4}>
            <HStack spacing={2}>
              <Text
                fontSize="md"
                fontWeight="bold"
                color={componentColors.form.formLabelColor}
              >
                Content Syndication Programs
                <Tooltip
                  aria-label={"automated-intent-delivery-tooltip"}
                  placement={"top-end"}
                  label={
                    <>
                      These campaigns are pulled from the lead validator
                      platform, Convertr, and cannot be edited within this
                      screen. If changes are needed, please reach out to your
                      customer success manager.
                    </>
                  }
                >
                  <InfoIcon w={4} h={4} verticalAlign={"center"} pl={1} />
                </Tooltip>
              </Text>
            </HStack>

            <Text
              fontSize="sm"
              fontWeight="md"
              color={componentColors.form.formLabelColor}
              pb={2}
            >
              Below is a list of connected Convertr campaign IDs as well as an
              option to enter recipient emails for automated intent data
              delivery that correspond with their live content syndication
              programs.
            </Text>

            {isPopulatedArray(campaignAdvancedSettings.convertrCampaigns) ? (
              <UnorderedList fontSize="sm" pl={4}>
                {campaignAdvancedSettings.convertrCampaigns.map((i) => (
                  <ListItem key={i.convertrCampaignId}>
                    <Text
                      fontSize="sm"
                      fontWeight="md"
                      color={componentColors.form.formLabelColor}
                    >
                      {" "}
                      {i.displayName} ({i.convertrCampaignId}) — leads required:{" "}
                      <b>{i.leadsRequired}</b>
                    </Text>
                  </ListItem>
                ))}
              </UnorderedList>
            ) : (
              <Box>
                <Text fontSize="sm">Not available</Text>
              </Box>
            )}

            <FormControl
              isInvalid={!!errors?.leadDomainCap?.message}
              isRequired
              mt={4}
            >
              <FormLabel
                fontSize="sm"
                fontWeight="md"
                color={componentColors.form.formLabelColor}
              >
                Lead Domain Cap
              </FormLabel>
              <NumberInput
                shadow="sm"
                size="sm"
                rounded="md"
                width="2xs"
                defaultValue={10}
                max={10}
                min={1}
                step={1}
              >
                <NumberInputField
                  {...register("leadDomainCap", { valueAsNumber: true })}
                />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>

              <FormErrorMessage>
                {errors?.leadDomainCap?.message}
              </FormErrorMessage>
            </FormControl>

            <HasAccess to="campaign.intentDataDelivery">
              <Divider my={8} />
              <FormControl mt={8} as={GridItem} maxW="50%" pr={2}>
                <DeliveryRecipients
                  title="Automated Intent Delivery"
                  subtitle="Intent Delivery Recipients"
                  tooltip={
                    <>
                      This automated email delivery system is for any
                      customer/user that is interested in receiving our intent
                      intelligence for accounts that have garnered leads during
                      their campaign. Please toggle “on” and input an email
                      address below to begin the weekly cadence email service.
                    </>
                  }
                  recipients={intentDataDeliveryRecipients}
                  onRecipientsChange={setIntentDataDeliveryRecipients}
                  isEnabled={hasAutomatedIntentDelivery}
                  onIsEnabledChange={setHasAutomatedIntentDelivery}
                />
              </FormControl>
            </HasAccess>

            <HasAccess to="campaign.editShowBuyingGroupContactDetails">
              <Divider my={8} />
              <FormControl mt={8} as={GridItem} maxW="50%" pr={2}>
                <HStack spacing={2}>
                  <Text
                    p="0"
                    m="0"
                    fontSize="md"
                    fontWeight="bold"
                    color={componentColors.form.formLabelColor}
                  >
                    Show buying group contact details
                    <Tooltip
                      aria-label={"automated-intent-delivery-tooltip"}
                      placement={"top-end"}
                      label={
                        <>
                          When enabled, this setting will display contact
                          details of the buying group insights in the accounts
                          section.
                        </>
                      }
                    >
                      <InfoIcon w={4} h={4} verticalAlign={"center"} pl={1} />
                    </Tooltip>
                  </Text>

                  <Switch
                    isChecked={showBuyingGroupContactDetails}
                    onChange={(e) =>
                      setShowBuyingGroupContactDetails(e.currentTarget.checked)
                    }
                  />
                </HStack>
              </FormControl>
            </HasAccess>

            <Divider my={8} />

            <HasAccess to="campaign.moatReport">
              <FormControl
                isInvalid={!!errors?.averageDealSize?.message}
                mt={4}
              >
                <FormLabel
                  fontSize="sm"
                  fontWeight="md"
                  color={componentColors.form.formLabelColor}
                >
                  Average Deal Size
                </FormLabel>
                <NumberInput
                  shadow="sm"
                  size="sm"
                  rounded="md"
                  width="2xs"
                  max={1_000_000_000_000}
                  min={1}
                  step={100}
                >
                  <NumberInputField
                    {...register("averageDealSize", { valueAsNumber: true })}
                  />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>

                <FormErrorMessage>
                  {errors?.averageDealSize?.message}
                </FormErrorMessage>
              </FormControl>

              <FormControl
                isInvalid={!!errors?.averageSalesCycleMonths?.message}
                mt={4}
              >
                <FormLabel
                  fontSize="sm"
                  fontWeight="md"
                  color={componentColors.form.formLabelColor}
                >
                  Averages Sales Cycle In Months
                </FormLabel>
                <NumberInput
                  shadow="sm"
                  size="sm"
                  rounded="md"
                  width="2xs"
                  max={12}
                  min={1}
                  step={1}
                >
                  <NumberInputField
                    {...register("averageSalesCycleMonths", {
                      valueAsNumber: true,
                    })}
                  />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>

                <FormErrorMessage>
                  {errors?.averageSalesCycleMonths?.message}
                </FormErrorMessage>
              </FormControl>

              <Divider my={8} />
            </HasAccess>

            <Grid
              mt={2}
              gap={4}
              alignItems="flex-end"
              templateColumns="repeat(2, 1fr)"
            >
              <FormControl as={GridItem}>
                <Controller
                  name="programmaticCampaigns"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <FilterAsyncTokenBased
                      label="Ad Solutions Campaign ID"
                      defaultOptions={[]}
                      currentValue={value.map((programmaticCampaign) => {
                        return {
                          value: programmaticCampaign,
                          label: programmaticCampaign,
                        };
                      })}
                      onFilterValuesChange={(e) => {
                        onChange((e || []).map((s) => s.value));
                      }}
                      isMulti={true}
                      dataRequest={listProgrammaticCampaigns([
                        DSPProvider.beeswax,
                        DSPProvider.tradedesk,
                        DSPProvider.xandr,
                      ])}
                      showLabel={true}
                    />
                  )}
                />
              </FormControl>
              <FormControl as={GridItem}>
                <Controller
                  name="innovidCampaigns"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <FilterAsyncTokenBased
                      label="Innovid Campaign ID"
                      defaultOptions={[]}
                      currentValue={value.map((programmaticCampaign) => {
                        return {
                          value: programmaticCampaign,
                          label: programmaticCampaign,
                        };
                      })}
                      onFilterValuesChange={(e) => {
                        onChange((e || []).map((s) => s.value));
                      }}
                      isMulti={true}
                      dataRequest={listProgrammaticCampaigns([
                        DSPProvider.innovid,
                      ])}
                      showLabel={true}
                    />
                  )}
                />
              </FormControl>
              <FormControl as={GridItem} isInvalid={!!errors?.pixelIds}>
                <Controller
                  name="pixelIds"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <FilterAsyncTokenBased
                      label="Pixel ID"
                      defaultOptions={[]}
                      currentValue={value.map((pixel) => ({
                        value: pixel,
                        label: pixel,
                      }))}
                      onFilterValuesChange={(e) => {
                        onChange((e || []).map((s) => s.label));
                      }}
                      isMulti={true}
                      dataRequest={listPixels(companyId)}
                      showLabel={true}
                      params={{
                        ...defaultListPixelsParams,
                      }}
                    />
                  )}
                />
              </FormControl>
            </Grid>

            <HasAccess to="buyingGroup">
              <Divider my={8} />
              <FormControl as={GridItem} maxW="50%" pr={2}>
                <DeliveryRecipients
                  title="Automated Buying Group Contact Delivery"
                  subtitle="Buying Group Delivery Recipients"
                  tooltip={
                    <>
                      This automated email delivery system is for any
                      customer/user that is interested in receiving our intent
                      intelligence for accounts that have garnered leads during
                      their campaign. Please toggle “on” and input an email
                      address below to begin the weekly cadence email service.
                    </>
                  }
                  recipients={buyingGroupDeliveryRecipients}
                  onRecipientsChange={setBuyingGroupDeliveryRecipients}
                  isEnabled={hasAutomatedBuyingGroupelivery}
                  onIsEnabledChange={setHasAutomatedBuyingGroupelivery}
                />
              </FormControl>
            </HasAccess>

            <HasAccess to="campaign.leadActivationDeliverySetting">
              <Divider my={8} />
              <FormControl as={GridItem} maxW="50%" pr={2}>
                <DeliveryRecipients
                  title="Lead Activation Summaries Delivery"
                  subtitle="Lead Activation Summaries Recipients"
                  tooltip={
                    <>
                      This automated email delivery system is for any
                      customer/user that is interested in receiving our lead
                      activation summaries. Please toggle “on” and input an
                      email address below to begin the weekly cadence email
                      service.
                    </>
                  }
                  recipients={leadActivationSummariesDeliveryRecipients}
                  onRecipientsChange={
                    setLeadActivationSummariesDeliveryRecipients
                  }
                  isEnabled={hasLeadActivationSummariesDeliveryEnabled}
                  onIsEnabledChange={
                    setHasLeadActivationSummariesDeliveryEnabled
                  }
                />
              </FormControl>
            </HasAccess>
            <HasAccess to="campaign.enableCustomAudience">
              <Divider my={8} />
              <FormControl as={GridItem} maxW="50%" pr={2} mt={8}>
                <HStack spacing={2}>
                  <Text
                    p="0"
                    m="0"
                    fontSize="md"
                    fontWeight="bold"
                    color={componentColors.form.formLabelColor}
                  >
                    Enable Custom Audience
                    <Tooltip
                      aria-label={"enable-custom-audience-tooltip"}
                      placement={"top-end"}
                      label={
                        <>
                          When enabled, this setting will allow you to create a
                          custom audience for your campaign.
                        </>
                      }
                    >
                      <InfoIcon w={4} h={4} verticalAlign={"center"} pl={1} />
                    </Tooltip>
                  </Text>
                  <Switch
                    isChecked={watch("customAudienceEnabled")}
                    onChange={(e) =>
                      setValue("customAudienceEnabled", e.currentTarget.checked)
                    }
                    isDisabled={d1Status !== Dynamic1Status.Completed}
                  />
                </HStack>
              </FormControl>
            </HasAccess>
            <HasAccess to="campaign.enableCustomAudience">
              <Divider my={8} />
              <FormControl as={GridItem} maxW="50%" pr={2} mt={8}>
                <HStack spacing={2}>
                  <Text
                    p="0"
                    m="0"
                    fontSize="md"
                    fontWeight="bold"
                    color={componentColors.form.formLabelColor}
                  >
                    Enable Custom International Audience
                    <Tooltip
                      aria-label={
                        "enable-custom-international-audience-tooltip"
                      }
                      placement={"top-end"}
                      label={
                        <>
                          When enabled, this setting will allow you to create a
                          custom international audience for your campaign.
                        </>
                      }
                    >
                      <InfoIcon w={4} h={4} verticalAlign={"center"} pl={1} />
                    </Tooltip>
                  </Text>
                  <Switch
                    isChecked={watch("customInternationalAudienceEnabled")}
                    onChange={(e) =>
                      setValue(
                        "customInternationalAudienceEnabled",
                        e.currentTarget.checked
                      )
                    }
                    isDisabled={d1Status !== Dynamic1Status.Completed}
                  />
                </HStack>
              </FormControl>
            </HasAccess>
            <HasAccess to="campaign.enableCustomAudiencePreview">
              <Divider my={8} />
              <FormControl as={GridItem} maxW="50%" pr={2} mt={8}>
                <HStack spacing={2}>
                  <Text
                    p="0"
                    m="0"
                    fontSize="md"
                    fontWeight="bold"
                    color={componentColors.form.formLabelColor}
                  >
                    Custom Audience Counts (Turnilo)
                    <Tooltip
                      aria-label={"enable-custom-audience-preview-tooltip"}
                      placement={"top-end"}
                      label={
                        <>
                          When enabled, this setting will allow you to create a
                          custom audience preview for your campaign.
                        </>
                      }
                    >
                      <InfoIcon w={4} h={4} verticalAlign={"center"} pl={1} />
                    </Tooltip>
                  </Text>
                  <Switch
                    isChecked={watch("customAudiencePreviewEnabled")}
                    onChange={(e) =>
                      setValue(
                        "customAudiencePreviewEnabled",
                        e.currentTarget.checked
                      )
                    }
                  />
                </HStack>
              </FormControl>
            </HasAccess>
          </chakra.form>
        </GridItem>
      </SimpleGrid>
    </>
  );
});

export { ActivationSettingsForm };
